import { useCallback } from 'react'
import { useAppDispatch, useAppSelector } from '../../store'
import { updateMultipleFeatureFlags } from '../../store/slices/uiSlice'
import {
  selectFeatureFlags,
  selectIsGamerEnabled,
  selectIsFacilitatorEnabled,
  selectIsLanguageSwitcherEnabled,
  selectIsLatestSessionFeedbackEnabled,
  selectIsGenerateReportsEnabled,
  selectIsExperimentalTeamFeedbackEnabled,
  selectIsAchievementsEnabled
} from '../../store/selectors/featureFlags'
import { getFeatureFlagsFromUrl } from '../libs/featureFlags'

/**
 * Hook to manage feature flags in Redux
 * Does not automatically read URL flags to avoid SSR issues
 * Call initializeFromUrl() manually when needed
 */
export const useFeatureFlags = () => {
  const dispatch = useAppDispatch()
  const featureFlags = useAppSelector(selectFeatureFlags)
  const isGamerEnabled = useAppSelector(selectIsGamerEnabled)
  const isFacilitatorEnabled = useAppSelector(selectIsFacilitatorEnabled)
  const isLanguageSwitcherEnabled = useAppSelector(selectIsLanguageSwitcherEnabled)
  const isLatestSessionFeedbackEnabled = useAppSelector(selectIsLatestSessionFeedbackEnabled)
  const isGenerateReportsEnabled = useAppSelector(selectIsGenerateReportsEnabled)
  const isExperimentalTeamFeedbackEnabled = useAppSelector(selectIsExperimentalTeamFeedbackEnabled)
  const isAchievementsEnabled = useAppSelector(selectIsAchievementsEnabled)

  // Manual function to initialize from URL when safe to do so
  const initializeFromUrl = useCallback(() => {
    const urlFlags = getFeatureFlagsFromUrl()
    if (Object.keys(urlFlags).length > 0) {
      // Only update flags that exist in our FeatureFlags interface
      const validFlags: Partial<typeof featureFlags> = {}

      if ('enableGamer' in urlFlags) {
        validFlags.enableGamer = urlFlags.enableGamer
      }
      if ('enableFacilitator' in urlFlags) {
        validFlags.enableFacilitator = urlFlags.enableFacilitator
      }
      if ('enableLanguageSwitcher' in urlFlags) {
        validFlags.enableLanguageSwitcher = urlFlags.enableLanguageSwitcher
      }
      if ('enableLatestSessionFeedback' in urlFlags) {
        validFlags.enableLatestSessionFeedback = urlFlags.enableLatestSessionFeedback
      }
      if ('enableGenerateReports' in urlFlags) {
        validFlags.enableGenerateReports = urlFlags.enableGenerateReports
      }
      if ('enableExperimentalTeamFeedback' in urlFlags) {
        validFlags.enableExperimentalTeamFeedback = urlFlags.enableExperimentalTeamFeedback
      }
      if ('enableAchievements' in urlFlags) {
        validFlags.enableAchievements = urlFlags.enableAchievements
      }

      if (Object.keys(validFlags).length > 0) {
        dispatch(updateMultipleFeatureFlags(validFlags))
      }
    }
  }, [dispatch])

  return {
    featureFlags,
    isGamerEnabled,
    isFacilitatorEnabled,
    isLanguageSwitcherEnabled,
    isLatestSessionFeedbackEnabled,
    isGenerateReportsEnabled,
    isExperimentalTeamFeedbackEnabled,
    isAchievementsEnabled,
    initializeFromUrl
  }
}
